import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import VerificationBadges from '../verification/VerificationBadges';

const MobileNav = ({ 
  isOpen, 
  onClose, 
  onShowAuth,
  onShowProfile,
  onShowReviews,
  onShowSettings,
  onShowVerification,
  onShowAdmin,
  onToggleAdminMode,
  onShowDashboard,
  onShowMap,
  isAdminMode,
  isDashboardActive,
  isMapActive
}) => {
  const { isAuthenticated, user, logout } = useAuth();
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    // Prevent body scroll when menu is open
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
      setIsClosing(false);
    }, 300);
  };

  const handleItemClick = (action) => {
    handleClose();
    setTimeout(() => {
      action();
    }, 100);
  };

  const handleLogout = () => {
    logout();
    handleClose();
  };

  if (!isOpen && !isClosing) return null;

  const backdropStyles = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 1000,
    opacity: isClosing ? 0 : 1,
    transition: 'opacity 300ms ease-in-out'
  };

  const navStyles = {
    position: 'fixed',
    top: 0,
    right: 0,
    bottom: 0,
    width: '280px',
    maxWidth: '80vw',
    backgroundColor: '#ffffff',
    boxShadow: '-4px 0 20px rgba(0, 0, 0, 0.15)',
    zIndex: 1001,
    transform: isClosing ? 'translateX(100%)' : 'translateX(0)',
    transition: 'transform 300ms ease-in-out',
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
    // Safe area support
    paddingTop: 'env(safe-area-inset-top)',
    paddingBottom: 'env(safe-area-inset-bottom)',
    paddingRight: 'env(safe-area-inset-right)'
  };

  const headerStyles = {
    padding: '24px 20px',
    backgroundColor: '#2196F3',
    color: '#ffffff',
    borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
  };

  const menuStyles = {
    flex: 1,
    padding: '16px 0'
  };

  const menuItemStyles = {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '16px 20px',
    color: '#212121',
    textDecoration: 'none',
    fontSize: '16px',
    fontWeight: '500',
    border: 'none',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    width: '100%',
    textAlign: 'left',
    minHeight: '56px', // Touch-friendly
    transition: 'background-color 150ms ease-in-out',
    // Touch improvements
    touchAction: 'manipulation',
    WebkitTapHighlightColor: 'transparent'
  };

  const menuItemHoverStyles = {
    backgroundColor: '#f5f5f5'
  };

  const iconStyles = {
    width: '24px',
    height: '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '18px'
  };

  const dividerStyles = {
    height: '1px',
    backgroundColor: '#e0e0e0',
    margin: '8px 0'
  };

  const footerStyles = {
    padding: '16px 20px',
    backgroundColor: '#f9f9f9',
    borderTop: '1px solid #e0e0e0'
  };

  const MenuItem = ({ icon, children, onClick, color = '#212121' }) => {
    const [isHovered, setIsHovered] = useState(false);

    return (
      <button
        style={{
          ...menuItemStyles,
          color,
          backgroundColor: isHovered ? '#f5f5f5' : 'transparent'
        }}
        onClick={onClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onTouchStart={() => setIsHovered(true)}
        onTouchEnd={() => setIsHovered(false)}
      >
        <div style={iconStyles}>{icon}</div>
        <span>{children}</span>
      </button>
    );
  };

  return (
    <div style={backdropStyles} onClick={handleClose}>
      <nav style={navStyles} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={headerStyles}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '8px'
          }}>
            <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>
              WanderVibe
            </h2>
            <button
              onClick={handleClose}
              style={{
                background: 'none',
                border: 'none',
                color: '#ffffff',
                fontSize: '24px',
                cursor: 'pointer',
                padding: '4px',
                borderRadius: '4px',
                minWidth: '32px',
                minHeight: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              ×
            </button>
          </div>
          
          {isAuthenticated ? (
            <div>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginBottom: '8px'
              }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '16px',
                  fontWeight: '600'
                }}>
                  {(user?.profile?.displayName || user?.username || 'U').charAt(0).toUpperCase()}
                </div>
                <div>
                  <div style={{ fontSize: '16px', fontWeight: '600' }}>
                    {user?.profile?.displayName || user?.username}
                  </div>
                  <div style={{ fontSize: '12px', opacity: 0.8 }}>
                    {user?.email}
                  </div>
                </div>
              </div>
              <VerificationBadges user={user} size="small" />
            </div>
          ) : (
            <div style={{ fontSize: '14px', opacity: 0.9 }}>
              Sign in to access all features
            </div>
          )}
        </div>

        {/* Menu Items */}
        <div style={menuStyles}>
          {isAuthenticated ? (
            <>
              {/* Main Navigation */}
              <MenuItem
                icon="🏠"
                onClick={() => handleItemClick(onShowDashboard)}
              >
                <span style={{ fontWeight: isDashboardActive ? 'bold' : 'normal' }}>
                  Dashboard {isDashboardActive && '•'}
                </span>
              </MenuItem>
              
              <MenuItem
                icon="🗺️"
                onClick={() => handleItemClick(onShowMap)}
              >
                <span style={{ fontWeight: isMapActive ? 'bold' : 'normal' }}>
                  Map View {isMapActive && '•'}
                </span>
              </MenuItem>
              
              <div style={dividerStyles}></div>
              
              <MenuItem
                icon="👤"
                onClick={() => handleItemClick(onShowProfile)}
              >
                Profile Settings
              </MenuItem>
              
              <MenuItem
                icon="📝"
                onClick={() => handleItemClick(onShowReviews)}
              >
                My Reviews
              </MenuItem>
              
              <MenuItem
                icon="✅"
                onClick={() => handleItemClick(onShowVerification)}
              >
                Verification Center
              </MenuItem>
              
              <MenuItem
                icon="⚙️"
                onClick={() => handleItemClick(onShowSettings)}
              >
                Account Settings
              </MenuItem>
              
              <div style={dividerStyles}></div>
              
              <MenuItem
                icon="🗺️"
                onClick={() => handleItemClick(() => {})}
              >
                Explore Map
              </MenuItem>
              
              <MenuItem
                icon="🔍"
                onClick={() => handleItemClick(() => {})}
              >
                Search Locations
              </MenuItem>
              
              <MenuItem
                icon="📊"
                onClick={() => handleItemClick(() => {})}
              >
                Safety Reports
              </MenuItem>
              
              {user?.role === 'admin' && (
                <>
                  <div style={dividerStyles}></div>
                  
                  <MenuItem
                    icon="👑"
                    onClick={() => handleItemClick(onShowAdmin)}
                  >
                    Admin Dashboard
                  </MenuItem>
                  
                  <MenuItem
                    icon={isAdminMode ? "🔒" : "🛡️"}
                    onClick={() => handleItemClick(onToggleAdminMode)}
                    color={isAdminMode ? "#F44336" : "#4CAF50"}
                  >
                    {isAdminMode ? 'Exit Admin Mode' : 'Admin Mode'}
                  </MenuItem>
                </>
              )}
              
              <div style={dividerStyles}></div>
              
              <MenuItem
                icon="🌙"
                onClick={() => handleItemClick(() => {})}
              >
                Dark Mode
              </MenuItem>
              
              <MenuItem
                icon="❓"
                onClick={() => handleItemClick(() => {})}
              >
                Help & Support
              </MenuItem>
              
              <MenuItem
                icon="🔐"
                onClick={() => handleItemClick(() => {})}
              >
                Privacy Policy
              </MenuItem>
              
              <div style={dividerStyles}></div>
              
              <MenuItem
                icon="🚪"
                onClick={() => handleItemClick(handleLogout)}
                color="#F44336"
              >
                Sign Out
              </MenuItem>
            </>
          ) : (
            <>
              <MenuItem
                icon="🔑"
                onClick={() => handleItemClick(onShowAuth)}
              >
                Sign In
              </MenuItem>
              
              <MenuItem
                icon="📝"
                onClick={() => handleItemClick(onShowAuth)}
              >
                Create Account
              </MenuItem>
              
              <div style={dividerStyles}></div>
              
              <MenuItem
                icon="🗺️"
                onClick={() => handleItemClick(() => {})}
              >
                Explore Map
              </MenuItem>
              
              <MenuItem
                icon="🔍"
                onClick={() => handleItemClick(() => {})}
              >
                Search Locations
              </MenuItem>
              
              <MenuItem
                icon="📊"
                onClick={() => handleItemClick(() => {})}
              >
                Safety Reports
              </MenuItem>
              
              <div style={dividerStyles}></div>
              
              <MenuItem
                icon="❓"
                onClick={() => handleItemClick(() => {})}
              >
                Help & Support
              </MenuItem>
              
              <MenuItem
                icon="🔐"
                onClick={() => handleItemClick(() => {})}
              >
                Privacy Policy
              </MenuItem>
            </>
          )}
        </div>

        {/* Footer */}
        <div style={footerStyles}>
          <div style={{
            fontSize: '12px',
            color: '#666666',
            textAlign: 'center',
            lineHeight: '1.4'
          }}>
            Travel Safety Platform v1.0
            <br />
            Making travel safer for everyone
          </div>
        </div>
      </nav>
    </div>
  );
};

export default MobileNav;